/**
 * D-1023-BUSY — the net under the pattern. A button's click handler is often `() => { setX(…); save(); }` — it starts a
 * request and returns nothing. While a click handler runs (the synchronous part, up to its first `await`), any
 * press the app sends (apiFetch with a method other than GET) is noted here, so the button can show "Saving…" until
 * those requests answer, whether or not the handler returned the promise.
 */
let collecting: Promise<unknown>[] | null = null;
/** Presses that only joined the scheduler's queue: the server's "OK" is not yet the outcome, so no "done" is claimed for them. */
const queued = new WeakSet<Promise<unknown>>();

/** Called by apiFetch for every request that is not a read. */
export function notePress(p: Promise<unknown>, joinsQueue = false): void {
  if (joinsQueue) queued.add(p);
  if (collecting) collecting.push(p);
}

/** True when every noted press was answered OK and none of them only joined the queue — the one case where "Added ✓" is honest. */
export function answeredForReal(sent: readonly Promise<unknown>[]): boolean {
  return sent.length > 0 && sent.every((p) => !queued.has(p));
}

/** Run a click handler and return what it returned, plus the presses it sent before its first `await`. */
export function collectPresses<T>(fn: () => T): { result: T; sent: Promise<unknown>[] } {
  const outer = collecting;
  const mine: Promise<unknown>[] = [];
  collecting = mine;
  try {
    return { result: fn(), sent: mine };
  } finally {
    collecting = outer;
  }
}

/** The press was sent and the server refused it; the page has already said so in its own words, so the button only comes back. */
export class PressRefused extends Error {
  constructor() {
    super('refused');
    this.name = 'PressRefused';
  }
}

/** Resolves when every noted press has answered; throws PressRefused if one was refused (so no done state is shown for it). */
export async function settled(sent: readonly Promise<unknown>[]): Promise<void> {
  if (!sent.length) return;
  const all = await Promise.allSettled(sent);
  if (all.some((x) => x.status === 'rejected')) throw new PressRefused();
}
